import React, { useState, useRef } from 'react';
import { GxZone, GxFeedItem, createNote } from '../../api/guestExperience';

interface LiveNoteInputProps {
  eventId: string;
  zones: GxZone[];
  onNoteCreated?: (item: GxFeedItem) => void;
}

const MAX_CHARS = 500;

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  row: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'stretch',
  },
  zoneSelect: {
    flex: '0 0 auto',
    minWidth: '120px',
    padding: '0.625rem 0.5rem',
    borderRadius: '8px',
    border: '1px solid #ccc',
    fontSize: '0.875rem',
    backgroundColor: '#fff',
    appearance: 'none' as const,
    WebkitAppearance: 'none' as const,
    backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23666\' d=\'M6 8L1 3h10z\'/%3E%3C/svg%3E")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
    paddingRight: '24px',
  },
  textInput: {
    flex: 1,
    padding: '0.625rem 0.75rem',
    borderRadius: '8px',
    border: '1px solid #ccc',
    fontSize: '0.875rem',
    resize: 'none' as const,
    minHeight: '40px',
    fontFamily: 'inherit',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  charCount: {
    fontSize: '0.75rem',
    color: '#999',
  },
  charCountWarning: {
    fontSize: '0.75rem',
    color: '#e65100',
    fontWeight: 600,
  },
  submitButton: {
    padding: '0.625rem 1.25rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
    minWidth: '80px',
    minHeight: '44px',
  },
  submitButtonDisabled: {
    padding: '0.625rem 1.25rem',
    backgroundColor: '#ccc',
    color: '#666',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'not-allowed',
    minWidth: '80px',
    minHeight: '44px',
  },
  errorMessage: {
    fontSize: '0.75rem',
    color: '#d32f2f',
    margin: 0,
    padding: '0.25rem 0',
  },
};

export function LiveNoteInput({ eventId, zones, onNoteCreated }: LiveNoteInputProps) {
  const [message, setMessage] = useState('');
  const [selectedZoneId, setSelectedZoneId] = useState<string>(zones[0]?.id || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const trimmedMessage = message.trim();
  const isDisabled = trimmedMessage.length === 0 || isSubmitting || !selectedZoneId;
  const charCount = message.length;
  const isOverLimit = charCount > MAX_CHARS;
  const isNearLimit = charCount > MAX_CHARS * 0.9;

  // Update selected zone if zones change and current selection is invalid
  React.useEffect(() => {
    if (zones.length > 0 && !zones.find((z) => z.id === selectedZoneId)) {
      setSelectedZoneId(zones[0].id);
    }
  }, [zones, selectedZoneId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isDisabled || isOverLimit) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const item = await createNote(eventId, {
        zoneId: selectedZoneId,
        message: trimmedMessage,
      });
      setMessage('');
      onNoteCreated?.(item);
      // Return focus to input
      inputRef.current?.focus();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to post note. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form style={styles.container} onSubmit={handleSubmit}>
      <div style={styles.row}>
        <select
          style={styles.zoneSelect}
          value={selectedZoneId}
          onChange={(e) => setSelectedZoneId(e.target.value)}
          aria-label="Select zone"
        >
          {zones.length === 0 && <option value="">No zones</option>}
          {zones.map((zone) => (
            <option key={zone.id} value={zone.id}>
              {zone.name}
            </option>
          ))}
        </select>
        <textarea
          ref={inputRef}
          style={styles.textInput}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Type a note..."
          rows={1}
          maxLength={MAX_CHARS}
          aria-label="Note message"
        />
      </div>
      <div style={styles.footer}>
        <span style={isNearLimit ? styles.charCountWarning : styles.charCount}>
          {charCount}/{MAX_CHARS}
        </span>
        <button
          type="submit"
          style={isDisabled || isOverLimit ? styles.submitButtonDisabled : styles.submitButton}
          disabled={isDisabled || isOverLimit}
        >
          {isSubmitting ? 'Posting...' : 'Post'}
        </button>
      </div>
      {error && <p style={styles.errorMessage}>{error}</p>}
    </form>
  );
}
